import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PropertyCard from './components/PropertyCard';
import SearchFilters from './components/SearchFilters';
import FacetedFilterPanel from './components/FacetedFilterPanel';

import { SearchResultsSkeleton, SearchEmptyState } from './components/PropertyCardSkeleton';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import GoBackButton from '../../components/GoBackButton';
import { useLanguage } from '../../contexts/LanguageContext';

const SORT_OPTIONS_EN = [
{ value: 'relevance', label: 'Relevance' },
{ value: 'price_asc', label: 'Price: Low to High' },
{ value: 'price_desc', label: 'Price: High to Low' },
{ value: 'date_desc', label: 'Newest First' },
{ value: 'date_asc', label: 'Oldest First' }];

const SORT_OPTIONS_BN = [
{ value: 'relevance', label: 'প্রাসঙ্গিকতা' },
{ value: 'price_asc', label: 'মূল্য: কম থেকে বেশি' },
{ value: 'price_desc', label: 'মূল্য: বেশি থেকে কম' },
{ value: 'date_desc', label: 'সর্বশেষ প্রথমে' },
{ value: 'date_asc', label: 'পুরনো প্রথমে' }];

const SORT_OPTIONS = {
  en: SORT_OPTIONS_EN,
  bn: SORT_OPTIONS_BN
};


const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, signOut } = useAuth();
  const { t, language } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('listinglens_sort') || 'relevance';
  });
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [savedSearches, setSavedSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('listinglens_saved_searches') || '[]');
    } catch {return [];}
  });
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [filters, setFilters] = useState({
    mode: searchParams?.get('mode') || 'buy',
    query: searchParams?.get('query') || '',
    region: searchParams?.get('region') || 'australia',
    location: searchParams?.get('location') || '',
    year: searchParams?.get('year') || '',
    minPrice: '',
    maxPrice: '',
    beds: '',
    baths: '',
    propertyType: ''
  });

  const [facetFilters, setFacetFilters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('listinglens_facet_filters') || 'null') || {
        minPrice: '',
        maxPrice: '',
        propertyAge: '',
        inspectionStatus: ''
      };
    } catch {return { minPrice: '', maxPrice: '', propertyAge: '', inspectionStatus: '' };}
  });

  const [savedFilterTemplates, setSavedFilterTemplates] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('listinglens_filter_templates') || '[]');
    } catch {return [];}
  });

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const mockUser = {
    id: user?.id || 1,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email || '',
    role: userProfile?.role || 'buyer'
  };

  useEffect(() => {
    fetchListings();
  }, [filters?.mode, filters?.query, filters?.location, filters?.region]);

  // Sync URL search params to filters when navigation occurs
  useEffect(() => {
    const newLocation = searchParams?.get('location') || '';
    const newMode = searchParams?.get('mode') || 'buy';
    const newQuery = searchParams?.get('query') || '';
    const newRegion = searchParams?.get('region') || 'australia';
    const newYear = searchParams?.get('year') || '';
    setFilters((prev) => ({
      ...prev,
      location: newLocation,
      mode: newMode,
      query: newQuery,
      region: newRegion,
      year: newYear
    }));
  }, [searchParams]);

  // Persist sort preference
  useEffect(() => {
    localStorage.setItem('listinglens_sort', sortBy);
  }, [sortBy]);

  // Persist saved searches
  useEffect(() => {
    localStorage.setItem('listinglens_saved_searches', JSON.stringify(savedSearches));
  }, [savedSearches]);

  // Persist facet filters
  useEffect(() => {
    localStorage.setItem('listinglens_facet_filters', JSON.stringify(facetFilters));
  }, [facetFilters]);

  // Persist filter templates
  useEffect(() => {
    localStorage.setItem('listinglens_filter_templates', JSON.stringify(savedFilterTemplates));
  }, [savedFilterTemplates]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (!e?.target?.closest('.sort-dropdown-container')) setShowSortDropdown(false);
      if (!e?.target?.closest('.saved-searches-container')) {
        setShowSavedSearches(false);
        setShowSaveForm(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const mockListings = [
  {
    id: '1',
    region: 'australia',
    properties: {
      address: '42 Harbour Street',
      suburb: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1850000,
    description_short: 'Stunning harbour views with modern finishes throughout',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c865bee3-1773909555410.png", type: 'photo', alt: 'Modern Sydney house exterior with harbour views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13efffede-1772837440495.png", type: 'photo', alt: 'Bright open-plan living room with harbour views' },
    { original_url: "https://images.unsplash.com/photo-1722764375583-146a5bc8d39c", type: 'photo', alt: 'Modern kitchen with stone benchtops and stainless appliances' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0d17207-1772167242133.png", type: 'photo', alt: 'Outdoor entertaining deck overlooking Sydney Harbour' }]

  },
  {
    id: '2',
    region: 'australia',
    properties: {
      address: '156 Beach Road',
      suburb: 'Bondi Beach',
      state: 'NSW',
      postcode: '2026',
      type: 'unit',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 1250000,
    description_short: 'Beachside living at its finest with ocean glimpses',
    media: [
    { original_url: "https://images.unsplash.com/photo-1727156706995-74ec72711fc4", type: 'photo', alt: 'Bondi Beach apartment building exterior near the ocean' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f998bd74-1764661904247.png", type: 'photo', alt: 'Bright apartment living area with ocean glimpses' },
    { original_url: "https://images.unsplash.com/photo-1601760562234-9814eea6663a", type: 'photo', alt: 'Modern kitchen with white cabinetry and stainless appliances' },
    { original_url: "https://images.unsplash.com/photo-1651766263459-a70e9f1d2e92", type: 'photo', alt: 'Cozy bedroom with coastal decor and natural light' }]

  },
  {
    id: '3',
    region: 'australia',
    properties: {
      address: '88 Mountain View Drive',
      suburb: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      type: 'house',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 2100000,
    description_short: 'Spacious family home with panoramic city views',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11f7d2efe-1772165918791.png", type: 'photo', alt: 'Spacious Melbourne family home exterior with double garage' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1705ea32a-1772167242434.png", type: 'photo', alt: 'Large open-plan family living and dining area' },
    { original_url: "https://images.unsplash.com/photo-1717429541792-5c59021d6ceb", type: 'photo', alt: 'Gourmet kitchen with island bench and city views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_169d130cb-1764673105782.png", type: 'photo', alt: 'Master bedroom with walk-in wardrobe and ensuite' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16b682000-1772166352678.png", type: 'photo', alt: 'Landscaped backyard with alfresco entertaining area' }]

  },
  {
    id: '4',
    region: 'uae',
    properties: {
      address: 'Palm Jumeirah Villa 12',
      suburb: 'Palm Jumeirah',
      state: 'Dubai',
      postcode: '',
      type: 'villa',
      beds: 5,
      baths: 4,
      parking: 3
    },
    listing_type: 'buy',
    price_or_rent: 8500000,
    description_short: 'Luxury beachfront villa with private pool on Palm Jumeirah',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16078fa54-1773044077044.png", type: 'photo', alt: 'Luxury Palm Jumeirah villa exterior with private pool and beach access' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1217475fc-1772167243094.png", type: 'photo', alt: 'Private infinity pool overlooking the Arabian Gulf at Palm Jumeirah villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1416f60b9-1772166785904.png", type: 'photo', alt: 'Grand living room with floor-to-ceiling windows and sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1052f63c4-1766213438869.png", type: 'photo', alt: 'Chef kitchen with marble countertops in luxury Dubai villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd574a97-1772167525575.png", type: 'photo', alt: 'Master suite with panoramic Arabian Gulf views' }]

  },
  {
    id: '5',
    region: 'uae',
    properties: {
      address: 'Downtown Dubai Tower 45, Unit 2201',
      suburb: 'Downtown Dubai',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 3200000,
    description_short: 'Stunning Burj Khalifa views from this modern high-rise apartment',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12d4ecc26-1772110246588.png", type: 'photo', alt: 'Downtown Dubai apartment building with Burj Khalifa views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_169be6201-1772109576210.png", type: 'photo', alt: 'Modern apartment living room with Burj Khalifa views through floor-to-ceiling windows' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2cc018-1772167242587.png", type: 'photo', alt: 'Contemporary kitchen with high-end appliances in Downtown Dubai apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d48360a8-1772167242049.png", type: 'photo', alt: 'Elegant bedroom with city views in Downtown Dubai apartment' }]

  },
  {
    id: '6',
    region: 'uae',
    properties: {
      address: 'Yas Island Villa 8',
      suburb: 'Yas Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 5800000,
    description_short: 'Contemporary villa near Yas Marina Circuit with sea views',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_137278c1c-1772166785072.png", type: 'photo', alt: 'Contemporary Yas Island villa exterior with landscaped garden and sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f45f1396-1772166358331.png", type: 'photo', alt: 'Private pool and outdoor terrace at Yas Island villa' },
    { original_url: "https://images.unsplash.com/photo-1721396185758-528e007b4973", type: 'photo', alt: 'Spacious living area with modern furnishings in Abu Dhabi villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_114760ca7-1772166784848.png", type: 'photo', alt: 'Modern kitchen with island bench in Yas Island villa' }]

  },
  // RENT listings
  {
    id: '7',
    region: 'australia',
    properties: {
      address: '12 Collins Street',
      suburb: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      type: 'apartment',
      beds: 2,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 2800,
    description_short: 'Modern apartment in the heart of Melbourne CBD',
    media: [
    { original_url: "https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9", type: 'photo', alt: 'Modern Melbourne CBD apartment building exterior on Collins Street' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e13741fb-1766566954518.png", type: 'photo', alt: 'Contemporary apartment living room with city views' },
    { original_url: "https://images.unsplash.com/photo-1719474815675-70b264f18962", type: 'photo', alt: 'Sleek kitchen with stone benchtops in Melbourne CBD apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11c9fcbdf-1767524752648.png", type: 'photo', alt: 'Comfortable bedroom with built-in wardrobes in city apartment' }]

  },
  {
    id: '8',
    region: 'australia',
    properties: {
      address: '5 Riverside Walk',
      suburb: 'Brisbane',
      state: 'QLD',
      postcode: '4000',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'rent',
    price_or_rent: 3200,
    description_short: 'Charming riverside home with large backyard and deck',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_120dd0d17-1772166784971.png", type: 'photo', alt: 'Charming Brisbane riverside house with large deck and backyard' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_153eed128-1772165917812.png", type: 'photo', alt: 'Spacious backyard deck with river views in Brisbane home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1529959b3-1772134670545.png", type: 'photo', alt: 'Open-plan living and dining area in Brisbane riverside home' },
    { original_url: "https://images.unsplash.com/photo-1722764386937-6da6ef2592c7", type: 'photo', alt: 'Updated kitchen with timber accents in Brisbane family home' }]

  },
  {
    id: '9',
    region: 'uae',
    properties: {
      address: 'Marina Walk Apartment 1502',
      suburb: 'Dubai Marina',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 85000,
    description_short: 'Stylish marina-view apartment in the vibrant Dubai Marina',
    media: [
    { original_url: "https://images.unsplash.com/photo-1657579813217-777707525f94", type: 'photo', alt: 'JBR beachfront apartment building with beach and sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d48360a8-1772167242049.png", type: 'photo', alt: 'Bright living room with beach views in JBR apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_196e241e7-1772156485299.png", type: 'photo', alt: 'Modern kitchen in Jumeirah Beach Residence apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec1dc504-1772166785003.png", type: 'photo', alt: 'Comfortable bedroom with sea views in JBR apartment' }]

  },
  {
    id: '10',
    region: 'uae',
    properties: {
      address: 'JBR The Walk, Unit 304',
      suburb: 'Jumeirah Beach Residence',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 120000,
    description_short: 'Beachfront apartment steps from the beach and dining',
    media: [
    { original_url: "https://images.unsplash.com/photo-1657579813217-777707525f94", type: 'photo', alt: 'JBR beachfront apartment building with beach and sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", type: 'photo', alt: 'Bright living room with beach views in JBR apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce695329-1772156479949.png", type: 'photo', alt: 'Modern kitchen in Jumeirah Beach Residence apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec1dc504-1772166785003.png", type: 'photo', alt: 'Comfortable bedroom with sea views in JBR apartment' }]

  },
  // COMMERCIAL listings
  {
    id: '11',
    region: 'australia',
    properties: {
      address: '200 George Street',
      suburb: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      type: 'office',
      beds: 0,
      baths: 2,
      parking: 10
    },
    listing_type: 'commercial',
    price_or_rent: 4500000,
    description_short: 'Premium CBD office space with stunning city views, 500sqm',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18a0c8860-1772131261916.png", type: 'photo', alt: 'Premium Sydney CBD office building exterior on George Street' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18368fe09-1767125848732.png", type: 'photo', alt: 'Modern open-plan office space with city views in Sydney CBD' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17fc80855-1772109662199.png", type: 'photo', alt: 'Executive boardroom with panoramic Sydney city views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc4cdfd6-1766529571369.png", type: 'photo', alt: 'Reception area and lobby of premium Sydney CBD office' }]

  },
  {
    id: '12',
    region: 'australia',
    properties: {
      address: '45 Industrial Drive',
      suburb: 'Dandenong',
      state: 'VIC',
      postcode: '3175',
      type: 'warehouse',
      beds: 0,
      baths: 1,
      parking: 20
    },
    listing_type: 'commercial',
    price_or_rent: 1800000,
    description_short: 'Modern warehouse and office complex with excellent freeway access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1651ef67e-1772132370725.png", type: 'photo', alt: 'Modern industrial warehouse exterior in Dandenong with large loading dock' },
    { original_url: "https://images.unsplash.com/photo-1606909225043-9420b2874334", type: 'photo', alt: 'Spacious warehouse interior with high clearance and concrete floor' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1aa0170-1767805821152.png", type: 'photo', alt: 'Office component of warehouse complex with modern fit-out' }]

  },
  {
    id: '13',
    region: 'uae',
    properties: {
      address: 'DIFC Gate Village, Suite 501',
      suburb: 'DIFC',
      state: 'Dubai',
      postcode: '',
      type: 'office',
      beds: 0,
      baths: 2,
      parking: 5
    },
    listing_type: 'commercial',
    price_or_rent: 9500000,
    description_short: 'Prestigious DIFC office in the heart of Dubai financial district',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1bed5ae17-1772156407832.png", type: 'photo', alt: 'Prestigious DIFC Gate Village office building exterior in Dubai financial district' },
    { original_url: "https://images.unsplash.com/photo-1580805799823-6b3b7ee8f022", type: 'photo', alt: 'Premium office interior with floor-to-ceiling windows in DIFC Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17fc80855-1772109662199.png", type: 'photo', alt: 'Executive boardroom with high-end finishes in DIFC office suite' }]

  },
  {
    id: '14',
    region: 'uae',
    properties: {
      address: 'Business Bay Tower, Floor 12',
      suburb: 'Business Bay',
      state: 'Dubai',
      postcode: '',
      type: 'office',
      beds: 0,
      baths: 3,
      parking: 8
    },
    listing_type: 'commercial',
    price_or_rent: 6200000,
    description_short: 'Grade A office space with canal views in Business Bay',
    media: [
    { original_url: "https://images.unsplash.com/photo-1665511253238-98a801d52920", type: 'photo', alt: 'Business Bay tower office building with Dubai Canal views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b1444c97-1772108460983.png", type: 'photo', alt: 'Grade A office floor with canal views in Business Bay Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1303bff8d-1770194513947.png", type: 'photo', alt: 'Modern office reception and common areas in Business Bay tower' }]

  },
  // OFF-MARKET listings
  {
    id: '15',
    region: 'australia',
    properties: {
      address: '7 Private Lane',
      suburb: 'Toorak',
      state: 'VIC',
      postcode: '3142',
      type: 'house',
      beds: 5,
      baths: 4,
      parking: 3
    },
    listing_type: 'off-market',
    price_or_rent: 6500000,
    description_short: 'Exclusive off-market estate in prestigious Toorak — private sale only',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1944e044c-1772165920121.png", type: 'photo', alt: 'Exclusive Toorak estate exterior with manicured gardens and grand entrance' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c87be4f4-1772165917799.png", type: 'photo', alt: 'Grand formal living room with high ceilings in Toorak estate' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_135aa9804-1772108461315.png", type: 'photo', alt: 'Gourmet kitchen with butler pantry in prestigious Toorak home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_170a65303-1772108463344.png", type: 'photo', alt: 'Luxurious master suite with dressing room in Toorak estate' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6bb70ec-1772156407730.png", type: 'photo', alt: 'Heated pool and landscaped gardens at Toorak private estate' }]

  },
  {
    id: '16',
    region: 'australia',
    properties: {
      address: '3 Clifftop Crescent',
      suburb: 'Mosman',
      state: 'NSW',
      postcode: '2088',
      type: 'unit',
      beds: 2,
      baths: 1,
      parking: 1
    },
    listing_type: 'off-market',
    price_or_rent: 780000,
    description_short: 'Charming heritage-style unit in vibrant Fremantle near the port',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dbc6f5c6-1773909554129.png", type: 'photo', alt: 'Heritage-style unit building in Mosman with harbour glimpses' },
    { original_url: "https://images.unsplash.com/photo-1573088422077-b6ed78408ff4", type: 'photo', alt: 'Charming living room with original features in Mosman unit' },
    { original_url: "https://images.unsplash.com/photo-1597723281483-9a00c1641bbd", type: 'photo', alt: 'Bedroom with natural light in heritage Mosman unit' }]

  },
  {
    id: '17',
    region: 'australia',
    properties: {
      address: '22 Joondalup Drive',
      suburb: 'Joondalup',
      state: 'WA',
      postcode: '6027',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'rent',
    price_or_rent: 2400,
    description_short: 'Modern family home in Joondalup with large backyard and double garage',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1019e3c47-1768202097726.png", type: 'photo', alt: 'Modern family home in Joondalup Perth with double garage and backyard' },
    { original_url: "https://images.unsplash.com/photo-1662557499734-36c037676f19", type: 'photo', alt: 'Open-plan living area in Joondalup family home' },
    { original_url: "https://images.unsplash.com/photo-1687946628944-67265aca2b07", type: 'photo', alt: 'Modern kitchen with breakfast bar in Joondalup home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_110a46071-1772166353713.png", type: 'photo', alt: 'Large backyard with lawn and patio in Joondalup family home' }]

  },
  // Adelaide listings
  {
    id: '22',
    region: 'australia',
    properties: {
      address: '5 North Terrace',
      suburb: 'Adelaide',
      state: 'SA',
      postcode: '5000',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 620000,
    description_short: 'Stylish CBD apartment steps from Adelaide\'s cultural precinct and parklands',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18033078f-1772108461712.png", type: 'photo', alt: 'Stylish Adelaide CBD apartment building on North Terrace near cultural precinct' },
    { original_url: "https://images.unsplash.com/photo-1605177948986-c8e933f4ecfd", type: 'photo', alt: 'Modern apartment living area with parkland views in Adelaide CBD' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d67ae978-1772165919127.png", type: 'photo', alt: 'Contemporary kitchen with stone benchtops in Adelaide apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15d3ef5fe-1768563049950.png", type: 'photo', alt: 'Bright bedroom with built-in robes in Adelaide CBD apartment' }]

  },
  {
    id: '23',
    region: 'australia',
    properties: {
      address: '18 Glenelg Beach Road',
      suburb: 'Glenelg',
      state: 'SA',
      postcode: '5045',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 890000,
    description_short: 'Beachside family home in Glenelg, minutes from Adelaide\'s best beaches',
    media: [
    { original_url: "https://images.unsplash.com/photo-1603815600486-e607fe079d42", type: 'photo', alt: 'Beachside family home in Glenelg Adelaide near the beach' },
    { original_url: "https://images.unsplash.com/photo-1613643043796-a370ee99ecbe", type: 'photo', alt: 'Modern living room with sea views in Glenelg beach home' },
    { original_url: "https://images.unsplash.com/photo-1648717108377-fb42483b849e", type: 'photo', alt: 'Outdoor entertaining area in Glenelg beachside home' }]

  },
  {
    id: '24',
    region: 'australia',
    properties: {
      address: '33 Norwood Parade',
      suburb: 'Norwood',
      state: 'SA',
      postcode: '5067',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'rent',
    price_or_rent: 2100,
    description_short: 'Elegant character home in Norwood with original features and modern updates',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12fad53d9-1772165658432.png", type: 'photo', alt: 'Elegant character home in Norwood Adelaide with original sandstone features' },
    { original_url: "https://images.unsplash.com/photo-1684837760253-d1a926702ac3", type: 'photo', alt: 'Character living room with high ceilings and original cornices in Norwood home' },
    { original_url: "https://images.unsplash.com/photo-1687946628944-67265aca2b07", type: 'photo', alt: 'Modern kitchen update in character Norwood Adelaide home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1439318d2-1772166353551.png", type: 'photo', alt: 'Spacious bedroom with original timber floors in Norwood character home' }]

  },
  // Canberra listings
  {
    id: '25',
    region: 'australia',
    properties: {
      address: '12 Barton Circuit',
      suburb: 'Canberra',
      state: 'ACT',
      postcode: '2600',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 950000,
    description_short: 'Spacious family home in Canberra\'s inner south with mountain views',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10bbdaf9a-1772156407067.png", type: 'photo', alt: 'Spacious family home in Canberra inner south with mountain backdrop' },
    { original_url: "https://images.unsplash.com/photo-1624411720833-3e47e6d42e09", type: 'photo', alt: 'Family living and dining area in Canberra home with mountain views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17d598f65-1772165917794.png", type: 'photo', alt: 'Contemporary kitchen with stone benchtops in Canberra apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11158d3e2-1764696086516.png", type: 'photo', alt: 'Backyard with lawn and garden in Canberra family home' }]

  },
  {
    id: '26',
    region: 'australia',
    properties: {
      address: '7 Kingston Foreshore',
      suburb: 'Kingston',
      state: 'ACT',
      postcode: '2604',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 720000,
    description_short: 'Modern waterfront apartment at Kingston Foreshore with lake views',
    media: [
    { original_url: "https://images.unsplash.com/photo-1641307662178-93b7f313ae06", type: 'photo', alt: 'Modern waterfront apartment building at Kingston Foreshore Canberra' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b22a461c-1772156483047.png", type: 'photo', alt: 'Bright apartment living room with lake views at Kingston Foreshore' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1234420f4-1771903679680.png", type: 'photo', alt: 'Contemporary kitchen in Kingston Foreshore waterfront apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18f97cbaf-1767599801024.png", type: 'photo', alt: 'Bedroom with lake views in Kingston Foreshore apartment Canberra' }]

  },
  {
    id: '27',
    region: 'australia',
    properties: {
      address: '45 Braddon Lane',
      suburb: 'Braddon',
      state: 'ACT',
      postcode: '2612',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 2200,
    description_short: 'Trendy apartment in Braddon\'s café and arts precinct, walk to city',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_143ccd339-1772156407808.png", type: 'photo', alt: 'Trendy apartment building in Braddon Canberra arts and cafe precinct' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1537bedaf-1770926755216.png", type: 'photo', alt: 'Modern studio apartment interior in Braddon Canberra' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1927c9f96-1772166352433.png", type: 'photo', alt: 'Compact kitchen with modern appliances in Braddon apartment' }]

  },
  // Hobart listings
  {
    id: '28',
    region: 'australia',
    properties: {
      address: '9 Battery Point Road',
      suburb: 'Hobart',
      state: 'TAS',
      postcode: '7004',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 750000,
    description_short: 'Historic sandstone cottage in Battery Point with harbour views and character charm',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_150360b73-1772156408092.png", type: 'photo', alt: 'Historic sandstone cottage in Battery Point Hobart with harbour views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11a26eaef-1772156406912.png", type: 'photo', alt: 'Character living room with fireplace in Battery Point Hobart cottage' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c1006f5e-1772156408729.png", type: 'photo', alt: 'Updated kitchen in historic Battery Point Hobart cottage' },
    { original_url: "https://images.unsplash.com/photo-1651766263459-a70e9f1d2e92", type: 'photo', alt: 'Cozy bedroom with harbour views in Battery Point cottage' }]

  },
  {
    id: '29',
    region: 'australia',
    properties: {
      address: '22 Sandy Bay Road',
      suburb: 'Sandy Bay',
      state: 'TAS',
      postcode: '7005',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 680000,
    description_short: 'Elegant family home in Sandy Bay with Derwent River views and large garden',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13cada412-1772156408261.png", type: 'photo', alt: 'Elegant family home in Sandy Bay Hobart with Derwent River views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12a2431b7-1770202230518.png", type: 'photo', alt: 'Spacious living room with river views in Sandy Bay Hobart home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1af2c3ca7-1765220539722.png", type: 'photo', alt: 'Modern kitchen in Sandy Bay family home Hobart' },
    { original_url: "https://images.unsplash.com/photo-1726093704797-3e0bd3908da3", type: 'photo', alt: 'Large garden with lawn and established trees in Sandy Bay home' }]

  },
  {
    id: '30',
    region: 'australia',
    properties: {
      address: '5 Salamanca Place',
      suburb: 'Salamanca',
      state: 'TAS',
      postcode: '7004',
      type: 'apartment',
      beds: 2,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 1800,
    description_short: 'Stylish apartment above Salamanca Market precinct with waterfront access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1871546ab-1772165918307.png", type: 'photo', alt: 'Stylish apartment above Salamanca Place Hobart waterfront precinct' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12366be13-1772165918805.png", type: 'photo', alt: 'Modern apartment living area near Salamanca Market Hobart' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14022ff2f-1772166354028.png", type: 'photo', alt: 'Kitchen in Salamanca Place apartment' }]

  },
  // Darwin listings
  {
    id: '31',
    region: 'australia',
    properties: {
      address: '3 Fannie Bay Drive',
      suburb: 'Darwin',
      state: 'NT',
      postcode: '0820',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 620000,
    description_short: 'Tropical lifestyle home in Fannie Bay with pool, large deck and sea breezes',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1751aa654-1765174983911.png", type: 'photo', alt: 'Tropical lifestyle home in Fannie Bay Darwin with pool and large deck' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3b4c010-1772156409023.png", type: 'photo', alt: 'Swimming pool and outdoor entertaining deck at Fannie Bay Darwin home' },
    { original_url: "https://images.unsplash.com/photo-1721396185758-528e007b4973", type: 'photo', alt: 'Spacious living area with modern furnishings in Fannie Bay Darwin home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16aec55bf-1772153566848.png", type: 'photo', alt: 'Modern kitchen in tropical Darwin home' }]

  },
  {
    id: '32',
    region: 'australia',
    properties: {
      address: '15 Cullen Bay Marina',
      suburb: 'Cullen Bay',
      state: 'NT',
      postcode: '0820',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 480000,
    description_short: 'Marina-front apartment in Cullen Bay with stunning sunset views over the harbour',
    media: [
    { original_url: "https://images.unsplash.com/photo-1709025618846-00adf5b8a14b", type: 'photo', alt: 'Marina-front apartment building in Cullen Bay Darwin with harbour views' },
    { original_url: "https://images.unsplash.com/photo-1648450098095-ce3f2306f617", type: 'photo', alt: 'Private beach and outdoor terrace at Cullen Bay marina apartment' },
    { original_url: "https://images.unsplash.com/photo-1668911492356-bacadc25f2af", type: 'photo', alt: 'Spacious living area in Cullen Bay marina apartment Darwin' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18f7f28cf-1772156483605.png", type: 'photo', alt: 'Modern kitchen in Cullen Bay marina apartment Darwin' }]

  },
  {
    id: '33',
    region: 'australia',
    properties: {
      address: '8 Parap Village Road',
      suburb: 'Parap',
      state: 'NT',
      postcode: '0820',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'rent',
    price_or_rent: 2600,
    description_short: 'Comfortable tropical home near Parap Village markets with covered outdoor entertaining',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10539e92c-1772156408738.png", type: 'photo', alt: 'Comfortable tropical home in Parap Darwin near village markets' },
    { original_url: "https://images.unsplash.com/photo-1567452340376-966e9b2446af", type: 'photo', alt: 'Living area in tropical Parap Darwin home with outdoor entertaining' },
    { original_url: "https://images.unsplash.com/photo-1629221729470-4e2059284c13", type: 'photo', alt: 'Covered outdoor entertaining area in Parap Darwin tropical home' }]

  },

  // ── DUBAI listings ──
  {
    id: 'dxb-1',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Palm Jumeirah Frond K, Villa 14',
      suburb: 'Palm Jumeirah',
      state: 'Dubai',
      postcode: '',
      type: 'villa',
      beds: 5,
      baths: 5,
      parking: 3
    },
    listing_type: 'buy',
    price_or_rent: 18500000,
    description_short: 'Iconic beachfront villa on Palm Jumeirah with private pool, direct beach access and panoramic Arabian Gulf views',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8add369-1771884478759.png", type: 'photo', alt: 'Iconic beachfront villa on Palm Jumeirah with private pool and Arabian Gulf views' },
    { original_url: "https://images.unsplash.com/photo-1566323526101-051f07ee8925", type: 'photo', alt: 'Private infinity pool with direct beach access at Palm Jumeirah villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_169be6201-1772109576210.png", type: 'photo', alt: 'Grand living room with panoramic Gulf views in Palm Jumeirah villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_161d80fa1-1772107528865.png", type: 'photo', alt: 'Luxury chef kitchen with marble island in Palm Jumeirah villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10800f4c7-1772167524866.png", type: 'photo', alt: 'Master suite with sea views and walk-in closet in Palm Jumeirah villa' }]

  },
  {
    id: 'dxb-2',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Burj Khalifa District, Tower 1, Unit 4802',
      suburb: 'Downtown Dubai',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 3,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 7800000,
    description_short: 'Ultra-premium apartment with direct Burj Khalifa and Dubai Fountain views in the heart of Downtown Dubai',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", type: 'photo', alt: 'Ultra-premium Downtown Dubai apartment with direct Burj Khalifa views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19b71a11d-1772110246421.png", type: 'photo', alt: 'Luxury living room with Burj Khalifa and Dubai Fountain views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17cc9c3a7-1772109575614.png", type: 'photo', alt: 'Premium kitchen with high-end appliances in Downtown Dubai apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", type: 'photo', alt: 'Master bedroom with Burj Khalifa views in Downtown Dubai luxury apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", type: 'photo', alt: 'Balcony with Dubai Fountain views in Downtown Dubai apartment' }]

  },
  {
    id: 'dxb-3',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Marina Walk Tower 2, Unit 1205',
      suburb: 'Dubai Marina',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 3400000,
    description_short: 'Stylish marina-view apartment in Dubai Marina with world-class amenities and vibrant waterfront lifestyle',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12d4ecc26-1772110246588.png", type: 'photo', alt: 'Stylish Marina Walk Tower apartment in Dubai Marina with marina views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f998bd74-1764661904247.png", type: 'photo', alt: 'Modern living room with marina views in Dubai Marina apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce695329-1772156479949.png", type: 'photo', alt: 'Contemporary kitchen in Dubai Marina apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1105cb397-1773044075748.png", type: 'photo', alt: 'Bedroom with marina views in Dubai Marina apartment' }]

  },
  {
    id: 'dxb-4',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Jumeirah Village Circle, District 10, Villa 88',
      suburb: 'Jumeirah Village Circle',
      state: 'Dubai',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 4,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 4200000,
    description_short: 'Modern independent villa in JVC with private garden, rooftop terrace and community park access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8add369-1771884478759.png", type: 'photo', alt: 'Modern independent villa in Jumeirah Village Circle with private garden' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a95b1b63-1772110248653.png", type: 'photo', alt: 'Private garden and rooftop terrace at JVC villa Dubai' },
    { original_url: "https://images.unsplash.com/photo-1600122086257-9a6726dd274f", type: 'photo', alt: 'Spacious living room in Jumeirah Village Circle villa Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_193ec81a2-1772156483325.png", type: 'photo', alt: 'Modern kitchen with island bench in JVC villa Dubai' }]

  },
  {
    id: 'dxb-5',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Business Bay Tower, Floor 12',
      suburb: 'Business Bay',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 95000,
    description_short: 'Sleek studio-plus apartment in Business Bay with Dubai Canal views and direct metro access',
    media: [
    { original_url: "https://images.unsplash.com/photo-1502465107197-846f6fde18ac", type: 'photo', alt: 'Sleek Business Bay apartment tower with Dubai Canal views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6ac69c9-1769225421874.png", type: 'photo', alt: 'Modern studio apartment interior with canal views in Business Bay Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_147864886-1772165658736.png", type: 'photo', alt: 'Compact modern kitchen in Business Bay Dubai apartment' }]

  },
  {
    id: 'dxb-7',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Palm Jumeirah Shoreline, Unit 1102',
      suburb: 'Palm Jumeirah',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 145000,
    description_short: 'Spacious 3-bedroom apartment on Palm Jumeirah with private beach access and sea views — perfect for families',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7bc9cb6-1772166356134.png", type: 'photo', alt: 'Palm Jumeirah Shoreline apartment with private beach access and sea views for rent' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10c4732d9-1772210224317.png", type: 'photo', alt: 'Spacious living room with sea views in Palm Jumeirah rental apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2ac8de5-1772166786449.png", type: 'photo', alt: 'Modern kitchen with island bench in Palm Jumeirah Shoreline rental apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1afe60e08-1772156483293.png", type: 'photo', alt: 'Modern bedroom in Palm Jumeirah Shoreline rental apartment' }]

  },
  {
    id: 'dxb-9',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Dubai Marina, Marina Promenade, Unit 2203',
      suburb: 'Dubai Marina',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 80000,
    description_short: 'Chic 1-bedroom apartment in Dubai Marina with full marina views, gym and pool — ideal for young professionals',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbd12076-1772109575907.png", type: 'photo', alt: 'Dubai Marina apartment tower with marina views available for rent' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8e62636-1772109577488.png", type: 'photo', alt: 'Modern living room with marina views in Dubai Marina rental apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16d84f84b-1772109576229.png", type: 'photo', alt: 'Stylish kitchen in Dubai Marina rental apartment' }]

  },
  {
    id: 'dxb-10',
    region: 'uae',
    emirate: 'Dubai',
    properties: {
      address: 'Jumeirah Village Circle, District 15, Apartment 301',
      suburb: 'Jumeirah Village Circle',
      state: 'Dubai',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 75000,
    description_short: 'Affordable 2-bedroom apartment in JVC with community park views — great value in a family-friendly neighbourhood',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15d202032-1772109576221.png", type: 'photo', alt: 'Affordable 2-bedroom apartment in Jumeirah Village Circle Dubai for rent' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa67c22f-1772109577302.png", type: 'photo', alt: 'Bright living room in Jumeirah Village Circle rental apartment Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_197e71cba-1772109575845.png", type: 'photo', alt: 'Modern kitchen in Jumeirah Village Circle rental apartment Dubai' }]

  },

  // ── ABU DHABI listings ──
  {
    id: 'auh-1',
    region: 'uae',
    emirate: 'Abu Dhabi',
    properties: {
      address: 'Saadiyat Island, Mamsha Al Saadiyat, Villa 7',
      suburb: 'Saadiyat Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'villa',
      beds: 5,
      baths: 5,
      parking: 3
    },
    listing_type: 'buy',
    price_or_rent: 14500000,
    description_short: 'Beachfront villa on Saadiyat Island near the Louvre Abu Dhabi with private beach and cultural district access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c370c8a2-1772156406303.png", type: 'photo', alt: 'Beachfront villa on Saadiyat Island Abu Dhabi near Louvre with private beach' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8115ee-1772110253296.png", type: 'photo', alt: 'Private beach and pool at Saadiyat Island villa Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11430d9b1-1772166785224.png", type: 'photo', alt: 'Grand living room with sea views in Saadiyat Island villa' },
    { original_url: "https://images.unsplash.com/photo-1722247478844-5cf4b981359a", type: 'photo', alt: 'Luxury kitchen in Saadiyat Island beachfront villa Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1adc52e66-1767448133159.png", type: 'photo', alt: 'Master bedroom with sea views in Saadiyat Island villa Abu Dhabi' }]

  },
  {
    id: 'auh-2',
    region: 'uae',
    emirate: 'Abu Dhabi',
    properties: {
      address: 'Al Reem Island, Shams Abu Dhabi, Tower C, Unit 1802',
      suburb: 'Al Reem Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'apartment',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 5200000,
    description_short: 'Spacious apartment on Al Reem Island with sea views, close to Abu Dhabi CBD and world-class amenities',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Spacious apartment tower on Al Reem Island Abu Dhabi with sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d48360a8-1772167242049.png", type: 'photo', alt: 'Bright living room with sea views in Al Reem Island apartment Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1615f68dc-1770163383698.png", type: 'photo', alt: 'Modern kitchen in Al Reem Island apartment Abu Dhabi' },
    { original_url: "https://images.unsplash.com/photo-1538430989507-797d5e4ba836", type: 'photo', alt: 'Spacious bedroom in Al Reem Island apartment Abu Dhabi' }]

  },
  {
    id: 'auh-3',
    region: 'uae',
    emirate: 'Abu Dhabi',
    properties: {
      address: 'Yas Island, Yas Acres, Plot 45',
      suburb: 'Yas Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 6800000,
    description_short: 'Contemporary villa in Yas Acres with golf course views, minutes from Yas Marina Circuit and Ferrari World',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14ca36ff0-1767536422886.png", type: 'photo', alt: 'Contemporary villa in Yas Acres Abu Dhabi with golf course views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd7b5394-1772165659062.png", type: 'photo', alt: 'Private pool and garden at Yas Acres villa Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_178090474-1772166786443.png", type: 'photo', alt: 'Modern living room in Yas Acres villa Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14142eac2-1764678676520.png", type: 'photo', alt: 'Gourmet kitchen in Yas Acres villa Abu Dhabi' }]

  },
  {
    id: 'auh-4',
    region: 'uae',
    emirate: 'Abu Dhabi',
    properties: {
      address: 'Corniche Road, Etihad Towers, Tower 3, Unit 2501',
      suburb: 'Corniche',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 130000,
    description_short: 'Prestigious Etihad Towers apartment on Abu Dhabi Corniche with stunning Arabian Gulf views and 5-star hotel services',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Prestigious Etihad Towers apartment on Abu Dhabi Corniche with Arabian Gulf views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Apartment living room with Gulf views in Etihad Towers Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14183eb14-1772166788310.png", type: 'photo', alt: 'Premium kitchen in Etihad Towers Corniche apartment Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", type: 'photo', alt: 'Elegant bedroom with Gulf views in Etihad Towers Abu Dhabi' }]

  },
  {
    id: 'auh-5',
    region: 'uae',
    emirate: 'Abu Dhabi',
    properties: {
      address: 'Al Maryah Island, Galleria Residences, Unit 1104',
      suburb: 'Al Maryah Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 85000,
    description_short: 'Modern apartment on Al Maryah Island — Abu Dhabi\'s financial hub — with Galleria Mall access and waterfront promenade',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_172df5bfa-1767818371345.png", type: 'photo', alt: 'Modern apartment on Al Maryah Island Abu Dhabi near Galleria Mall' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16dc130f5-1770194513252.png", type: 'photo', alt: 'Contemporary apartment living area on Al Maryah Island Abu Dhabi' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17b20bba3-1772153562902.png", type: 'photo', alt: 'Modern kitchen in Al Maryah Island Galleria Residences Abu Dhabi' }]

  },

  // ── SHARJAH listings ──
  {
    id: 'shj-1',
    region: 'uae',
    emirate: 'Sharjah',
    properties: {
      address: 'Al Majaz Waterfront, Tower 5, Unit 802',
      suburb: 'Al Majaz',
      state: 'Sharjah',
      postcode: '',
      type: 'apartment',
      beds: 3,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 1450000,
    description_short: 'Spacious apartment on Al Majaz Waterfront with Khalid Lagoon views and walking distance to Sharjah Art Museum',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1799da7ee-1772110247146.png", type: 'photo', alt: 'Spacious apartment on Al Majaz Waterfront Sharjah with Khalid Lagoon views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1241f1af3-1766163578033.png", type: 'photo', alt: 'Bright living room with lagoon views in Al Majaz Sharjah apartment' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19b90787d-1772166353583.png", type: 'photo', alt: 'Modern kitchen in Al Majaz Waterfront apartment Sharjah' },
    { original_url: "https://images.unsplash.com/photo-1612301988752-5a5b19021f45", type: 'photo', alt: 'Spacious bedroom in Al Majaz Waterfront apartment Sharjah' }]

  },
  {
    id: 'shj-2',
    region: 'uae',
    emirate: 'Sharjah',
    properties: {
      address: 'Aljada Community, Madar District, Villa 22',
      suburb: 'Aljada',
      state: 'Sharjah',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 2200000,
    description_short: 'Family villa in Aljada — Sharjah\'s largest mixed-use development — with community park, retail and entertainment hub',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1188f28dd-1772108459855.png", type: 'photo', alt: 'Family villa in Aljada Sharjah largest mixed-use development with community park' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1afdd5523-1772165918799.png", type: 'photo', alt: 'Garden and outdoor area at Aljada villa Sharjah' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12a2431b7-1770202230518.png", type: 'photo', alt: 'Spacious living room with river views in Sandy Bay Hobart home' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_197e71cba-1772109575845.png", type: 'photo', alt: 'Modern kitchen in Aljada family villa Sharjah' }]

  },
  {
    id: 'shj-3',
    region: 'uae',
    emirate: 'Sharjah',
    properties: {
      address: 'Muwaileh Commercial, Al Zahia, Unit 305',
      suburb: 'Muwaileh',
      state: 'Sharjah',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 42000,
    description_short: 'Affordable modern apartment in Muwaileh near University City — ideal for families and professionals commuting to Dubai',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_123726ee3-1772156406473.png", type: 'photo', alt: 'Affordable modern apartment in Muwaileh Sharjah near University City' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea8389d5-1769257683471.png", type: 'photo', alt: 'Modern apartment living area in Muwaileh Sharjah' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17cc9c3a7-1772109575614.png", type: 'photo', alt: 'Kitchen in Muwaileh apartment Sharjah' }]

  },
  {
    id: 'shj-4',
    region: 'uae',
    emirate: 'Sharjah',
    properties: {
      address: 'Tilal City, Phase 2, Plot 88',
      suburb: 'Tilal City',
      state: 'Sharjah',
      postcode: '',
      type: 'villa',
      beds: 5,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 3100000,
    description_short: 'Generous plot villa in Tilal City with freehold ownership — Sharjah\'s first freehold community for all nationalities',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1188f28dd-1772108459855.png", type: 'photo', alt: 'Generous villa in Tilal City Sharjah first freehold community for all nationalities' },
    { original_url: "https://images.unsplash.com/photo-1721396185758-528e007b4973", type: 'photo', alt: 'Spacious living area in Tilal City villa Sharjah' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_197e71cba-1772109575845.png", type: 'photo', alt: 'Modern kitchen in Tilal City villa Sharjah' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1955527a1-1772156483253.png", type: 'photo', alt: 'Large plot garden in Tilal City villa Sharjah' }]

  },

  // ── AJMAN listings ──
  {
    id: 'ajm-1',
    region: 'uae',
    emirate: 'Ajman',
    properties: {
      address: 'Ajman Corniche Road, Al Khor Tower, Unit 604',
      suburb: 'Ajman Corniche',
      state: 'Ajman',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 680000,
    description_short: 'Affordable sea-view apartment on Ajman Corniche — excellent rental yield potential with growing expat demand',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_137dd64cb-1772167522784.png", type: 'photo', alt: 'Affordable sea-view apartment on Ajman Corniche with excellent rental yield' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Apartment living room with sea views on Ajman Corniche' },
    { original_url: "https://images.unsplash.com/photo-1722603931793-3b0efed4e0ce", type: 'photo', alt: 'Kitchen in Ajman Corniche apartment' }]

  },
  {
    id: 'ajm-2',
    region: 'uae',
    emirate: 'Ajman',
    properties: {
      address: 'Emirates City, Lavender Tower, Unit 1201',
      suburb: 'Emirates City',
      state: 'Ajman',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 380000,
    description_short: 'Budget-friendly apartment in Emirates City — one of the UAE\'s most affordable freehold communities with high ROI',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_14c66c039-1764743881937.png", type: 'photo', alt: 'Budget-friendly apartment in Emirates City Ajman affordable freehold community' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce7fb7fa-1771884480287.png", type: 'photo', alt: 'Compact apartment interior in Emirates City Ajman' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15cf3eb1d-1772109576216.png", type: 'photo', alt: 'Bedroom in Emirates City Ajman apartment' }]

  },
  {
    id: 'ajm-3',
    region: 'uae',
    emirate: 'Ajman',
    properties: {
      address: 'Al Hamidiyah, Garden City Villa 15',
      suburb: 'Al Hamidiyah',
      state: 'Ajman',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1200000,
    description_short: 'Spacious family villa in Al Hamidiyah with large garden — Ajman\'s most sought-after residential neighbourhood',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_165af46a5-1765097003513.png", type: 'photo', alt: 'Spacious family villa in Al Hamidiyah Ajman with large garden' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d81edbdb-1772110253077.png", type: 'photo', alt: 'Family living room in Al Hamidiyah villa Ajman' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ddf7e2c1-1772156407137.png", type: 'photo', alt: 'Kitchen in Al Hamidiyah family villa Ajman' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8add369-1771884478759.png", type: 'photo', alt: 'Large garden at Al Hamidiyah villa Ajman' }]

  },
  {
    id: 'ajm-4',
    region: 'uae',
    emirate: 'Ajman',
    properties: {
      address: 'Al Nuaimiya, Tower 3, Unit 502',
      suburb: 'Al Nuaimiya',
      state: 'Ajman',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 28000,
    description_short: 'Well-maintained apartment in Al Nuaimiya — Ajman\'s commercial hub — with easy access to Sharjah and Dubai highways',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Well-maintained apartment in Al Nuaimiya Ajman near Sharjah and Dubai highways' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Apartment living area in Al Nuaimiya Ajman' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Bedroom in Al Nuaimiya apartment Ajman' }]

  },

  // ── RAS AL KHAIMAH listings ──
  {
    id: 'rak-1',
    region: 'uae',
    emirate: 'Ras Al Khaimah',
    properties: {
      address: 'Al Marjan Island, Bab Al Bahr, Villa 12',
      suburb: 'Al Marjan Island',
      state: 'Ras Al Khaimah',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 4,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 4500000,
    description_short: 'Stunning resort villa on Al Marjan Island — RAK\'s premier destination — near the upcoming Wynn Casino resort',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_118c870d0-1772108460576.png", type: 'photo', alt: 'Stunning resort villa on Al Marjan Island Ras Al Khaimah near Wynn Casino resort' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8115ee-1772110253296.png", type: 'photo', alt: 'Private pool and beach access at Al Marjan Island villa RAK' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16d84f84b-1772109576229.png", type: 'photo', alt: 'Living room with sea views in Al Marjan Island villa Ras Al Khaimah' },
    { original_url: "https://images.unsplash.com/photo-1609347744425-175ecbd3cc0e", type: 'photo', alt: 'Modern kitchen in Al Marjan Island villa Ras Al Khaimah' }]

  },
  {
    id: 'rak-2',
    region: 'uae',
    emirate: 'Ras Al Khaimah',
    properties: {
      address: 'Mina Al Arab, Flamingo Villas, Unit 8',
      suburb: 'Mina Al Arab',
      state: 'Ras Al Khaimah',
      postcode: '',
      type: 'villa',
      beds: 3,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 2800000,
    description_short: 'Waterfront villa in Mina Al Arab with mangrove views, private beach access and eco-resort lifestyle',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8115ee-1772110253296.png", type: 'photo', alt: 'Waterfront villa in Mina Al Arab Ras Al Khaimah with mangrove views and private beach' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2b1a64e-1772156410678.png", type: 'photo', alt: 'Private beach and outdoor terrace at Mina Al Arab villa RAK' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_173645b21-1767662991900.png", type: 'photo', alt: 'Spacious living room in Mina Al Arab waterfront villa Ras Al Khaimah' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1052f63c4-1766213438869.png", type: 'photo', alt: 'Kitchen in Mina Al Arab villa Ras Al Khaimah' }]

  },
  {
    id: 'rak-3',
    region: 'uae',
    emirate: 'Ras Al Khaimah',
    properties: {
      address: 'RAK City Centre, Al Nakheel, Tower B, Unit 905',
      suburb: 'Al Nakheel',
      state: 'Ras Al Khaimah',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 950000,
    description_short: 'Modern apartment in RAK City Centre with Hajar Mountain backdrop — affordable entry into a fast-growing tourism market',
    media: [
    { original_url: "https://images.unsplash.com/photo-1578725763208-0923c930cd53", type: 'photo', alt: 'Modern apartment in Ras Al Khaimah City Centre with Hajar Mountain backdrop' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_169be6201-1772109576210.png", type: 'photo', alt: 'Apartment living room with mountain views in RAK City Centre' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17cc9c3a7-1772109575614.png", type: 'photo', alt: 'Kitchen in RAK City Centre apartment Ras Al Khaimah' }]

  },
  {
    id: 'rak-4',
    region: 'uae',
    emirate: 'Ras Al Khaimah',
    properties: {
      address: 'Hayat Island, Anantara Residences, Unit 601',
      suburb: 'Hayat Island',
      state: 'Ras Al Khaimah',
      postcode: '',
      type: 'apartment',
      beds: 1,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 65000,
    description_short: 'Luxury serviced apartment on Hayat Island with Anantara resort amenities, beach access and stunning sea views',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8aa885b-1765192413094.png", type: 'photo', alt: 'Luxury serviced apartment on Hayat Island Ras Al Khaimah with Anantara resort amenities' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Resort-style apartment interior on Hayat Island RAK with sea views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Bedroom with sea views in Anantara Residences Hayat Island RAK' }]

  },

  // ── FUJAIRAH listings ──
  {
    id: 'fjr-1',
    region: 'uae',
    emirate: 'Fujairah',
    properties: {
      address: 'Fujairah Corniche, Al Dana Tower, Unit 702',
      suburb: 'Fujairah Corniche',
      state: 'Fujairah',
      postcode: '',
      type: 'apartment',
      beds: 3,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 1100000,
    description_short: 'East coast apartment on Fujairah Corniche with Indian Ocean views — unique opportunity in UAE\'s only east-coast emirate',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'East coast apartment on Fujairah Corniche with Indian Ocean views' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Apartment living room with ocean views on Fujairah Corniche' },
    { original_url: "https://images.unsplash.com/photo-1722603931793-3b0efed4e0ce", type: 'photo', alt: 'Kitchen in Fujairah Corniche apartment' },
    { original_url: "https://images.unsplash.com/photo-1611444756156-bbe40e0a2e56", type: 'photo', alt: 'Bedroom with ocean views in Fujairah Corniche apartment' }]

  },
  {
    id: 'fjr-2',
    region: 'uae',
    emirate: 'Fujairah',
    properties: {
      address: 'Dibba Al Fujairah, Mountain View Villas, Villa 5',
      suburb: 'Dibba Al Fujairah',
      state: 'Fujairah',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1850000,
    description_short: 'Scenic villa in Dibba with Hajar Mountain views and proximity to world-class diving sites and Musandam border',
    media: [
    { original_url: "https://images.unsplash.com/photo-1621412524294-d5608a29160e", type: 'photo', alt: 'Scenic villa in Dibba Al Fujairah with Hajar Mountain views near diving sites' },
    { original_url: "https://images.unsplash.com/photo-1604145195376-e2c8195adf29", type: 'photo', alt: 'Outdoor terrace with mountain views at Dibba Al Fujairah villa' },
    { original_url: "https://images.unsplash.com/photo-1613013441633-785518cf90b3", type: 'photo', alt: 'Living room in Dibba Al Fujairah mountain view villa' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1cae45aa1-1772156483663.png", type: 'photo', alt: 'Modern kitchen with island bench in Dibba Al Fujairah villa' }]

  },
  {
    id: 'fjr-3',
    region: 'uae',
    emirate: 'Fujairah',
    properties: {
      address: 'Fujairah City, Al Faseel Road, Unit 204',
      suburb: 'Al Faseel',
      state: 'Fujairah',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 1,
      parking: 1
    },
    listing_type: 'rent',
    price_or_rent: 35000,
    description_short: 'Peaceful apartment in Fujairah City — tranquil east coast living with mountain scenery and uncrowded beaches nearby',
    media: [
    { original_url: "https://images.unsplash.com/photo-1724404461355-83783d132b3d", type: 'photo', alt: 'Peaceful apartment in Fujairah City with mountain scenery and beaches nearby' },
    { original_url: "https://images.unsplash.com/photo-1592839656073-833413ae8874", type: 'photo', alt: 'Apartment living area in Al Faseel Fujairah City' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Bedroom in Al Faseel apartment Fujairah City' }]

  },
  {
    id: 'fjr-4',
    region: 'uae',
    emirate: 'Fujairah',
    properties: {
      address: 'Mina Saqr Free Zone, Industrial Plot 22',
      suburb: 'Mina Saqr',
      state: 'Fujairah',
      postcode: '',
      type: 'warehouse',
      beds: 0,
      baths: 1,
      parking: 10
    },
    listing_type: 'commercial',
    price_or_rent: 2200000,
    description_short: 'Strategic commercial warehouse in Fujairah Free Zone — UAE\'s key oil bunkering hub with direct port access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17e2e6ab9-1772156483405.png", type: 'photo', alt: 'Commercial warehouse in Fujairah Free Zone with direct port access' },
    { original_url: "https://images.unsplash.com/photo-1659006996404-642b0af1175b", type: 'photo', alt: 'Warehouse interior in Mina Saqr Fujairah Free Zone' }]

  },

  // ── UMM AL QUWAIN listings ──
  {
    id: 'uaq-1',
    region: 'uae',
    emirate: 'Umm Al Quwain',
    properties: {
      address: 'UAQ Marina, Dreamland Residences, Villa 3',
      suburb: 'UAQ Marina',
      state: 'Umm Al Quwain',
      postcode: '',
      type: 'villa',
      beds: 3,
      baths: 3,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1600000,
    description_short: 'Waterfront villa in UAQ Marina — UAE\'s most affordable emirate with untouched coastline and serene lagoon lifestyle',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16558e1ef-1764793853208.png", type: 'photo', alt: 'Waterfront villa in UAQ Marina Umm Al Quwain with untouched coastline and lagoon views' },
    { original_url: "https://images.unsplash.com/photo-1612348095033-a358256b2fd0", type: 'photo', alt: 'Outdoor terrace with lagoon views at UAQ Marina villa' },
    { original_url: "https://images.unsplash.com/photo-1660731513683-4cb0c9ac09b8", type: 'photo', alt: 'Living room in UAQ Marina villa Umm Al Quwain' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e848c26e-1772108459030.png", type: 'photo', alt: 'Kitchen in UAQ Marina villa Umm Al Quwain' }]

  },
  {
    id: 'uaq-2',
    region: 'uae',
    emirate: 'Umm Al Quwain',
    properties: {
      address: 'Al Salamah, Corniche Tower, Unit 405',
      suburb: 'Al Salamah',
      state: 'Umm Al Quwain',
      postcode: '',
      type: 'apartment',
      beds: 2,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 520000,
    description_short: 'Affordable sea-view apartment in UAQ — lowest entry price in the UAE with strong capital appreciation potential',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Affordable sea-view apartment in Umm Al Quwain with strong capital appreciation' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Apartment living room with sea views in Al Salamah Umm Al Quwain' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_197e71cba-1772109575845.png", type: 'photo', alt: 'Modern kitchen in Al Salamah Corniche Tower Umm Al Quwain' }]

  },
  {
    id: 'uaq-3',
    region: 'uae',
    emirate: 'Umm Al Quwain',
    properties: {
      address: 'UAQ Free Trade Zone, Warehouse Block C, Unit 8',
      suburb: 'UAQ Free Trade Zone',
      state: 'Umm Al Quwain',
      postcode: '',
      type: 'warehouse',
      beds: 0,
      baths: 1,
      parking: 8
    },
    listing_type: 'commercial',
    price_or_rent: 850000,
    description_short: 'Low-cost commercial warehouse in UAQ Free Trade Zone — ideal for SMEs seeking affordable UAE base with 100% foreign ownership',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18d15918e-1772108461685.png", type: 'photo', alt: 'Commercial warehouse in UAQ Free Trade Zone with 100% foreign ownership for SMEs' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18d15918e-1772108461685.png", type: 'photo', alt: 'Warehouse interior in UAQ Free Trade Zone Umm Al Quwain' }]

  },
  {
    id: 'uaq-4',
    region: 'uae',
    emirate: 'Umm Al Quwain',
    properties: {
      address: 'Falaj Al Mualla, Al Raudah Villa 18',
      suburb: 'Falaj Al Mualla',
      state: 'Umm Al Quwain',
      postcode: '',
      type: 'villa',
      beds: 4,
      baths: 3,
      parking: 2
    },
    listing_type: 'rent',
    price_or_rent: 38000,
    description_short: 'Spacious family villa in Falaj Al Mualla — quiet residential area with large plot, ideal for families seeking space at low cost',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17518d375-1772156406306.png", type: 'photo', alt: 'Spacious family villa in Falaj Al Mualla Umm Al Quwain with large plot' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d54873ba-1772156406563.png", type: 'photo', alt: 'Family living room in Falaj Al Mualla villa Umm Al Quwain' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8add369-1771884478759.png", type: 'photo', alt: 'Large plot garden in Falaj Al Mualla villa Umm Al Quwain' }]

  },
  // SELL listings
  {
    id: 'sell-1',
    region: 'australia',
    properties: {
      address: '18 Rosewood Avenue',
      suburb: 'Sydney',
      state: 'NSW',
      postcode: '2060',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1650000,
    description_short: 'Charming family home in sought-after North Sydney — ready to sell, priced to move',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15d655a5d-1772108899226.png", type: 'photo', alt: 'Family home exterior in North Sydney ready for sale' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11df829d5-1772108900447.png", type: 'photo', alt: 'Bright living room in North Sydney family home' }]
  },
  {
    id: 'sell-2',
    region: 'australia',
    properties: {
      address: '72 Bayside Crescent',
      suburb: 'Melbourne',
      state: 'VIC',
      postcode: '3186',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 1
    },
    listing_type: 'buy',
    price_or_rent: 1120000,
    description_short: 'Stylish bayside home in Brighton — vendor motivated, inspect today',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dae2b47f-1772165916115.png", type: 'photo', alt: 'Stylish Brighton bayside home exterior for sale' },
    { original_url: "https://images.unsplash.com/photo-1723641876143-e92eb116a51c", type: 'photo', alt: 'Open-plan living area in Brighton home for sale' }]
  },
  {
    id: 'sell-3',
    region: 'australia',
    properties: {
      address: '9 Sunridge Court',
      suburb: 'Brisbane',
      state: 'QLD',
      postcode: '4069',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 980000,
    description_short: 'Spacious Queensland home on large block — perfect for upsizers or investors',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_169cee7af-1772166785123.png", type: 'photo', alt: 'Spacious Queensland home on large block for sale in Brisbane' },
    { original_url: "https://images.unsplash.com/photo-1513334910040-0b9267213e4d", type: 'photo', alt: 'Backyard and deck of Brisbane home for sale' }]
  },
  {
    id: 'sell-4',
    region: 'australia',
    properties: {
      address: '34 Coastal Drive',
      suburb: 'Perth',
      state: 'WA',
      postcode: '6018',
      type: 'house',
      beds: 4,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 1350000,
    description_short: 'Modern coastal home in Scarborough — ocean glimpses, walk to beach',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12b0f7862-1772165916208.png", type: 'photo', alt: 'Modern coastal home in Scarborough Perth for sale' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17e97f054-1767446906687.png", type: 'photo', alt: 'Living room of coastal Perth home for sale' }]
  },
  {
    id: 'sell-5',
    region: 'australia',
    properties: {
      address: '5 Glenview Terrace',
      suburb: 'Canberra',
      state: 'ACT',
      postcode: '2600',
      type: 'house',
      beds: 3,
      baths: 2,
      parking: 2
    },
    listing_type: 'buy',
    price_or_rent: 870000,
    description_short: 'Well-maintained family home in Canberra inner north — government sector location',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13cb689b2-1767985558627.png", type: 'photo', alt: 'Family home in Canberra ACT inner north for sale' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fab8383a-1772167241872.png", type: 'photo', alt: 'Living area of Canberra home for sale' }]
  },
  {
    id: 'sell-6',
    region: 'uae',
    properties: {
      address: 'Emirates Hills Villa 22',
      suburb: 'Emirates Hills',
      state: 'Dubai',
      postcode: '',
      type: 'villa',
      beds: 6,
      baths: 5,
      parking: 4
    },
    listing_type: 'buy',
    price_or_rent: 12500000,
    description_short: 'Prestigious Emirates Hills mansion — golf course views, motivated seller',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8115ee-1772110253296.png", type: 'photo', alt: 'Prestigious Emirates Hills mansion exterior for sale in Dubai' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_10871d0ed-1764729353423.png", type: 'photo', alt: 'Golf course views from Emirates Hills villa for sale' }]
  },
  {
    id: 'sell-7',
    region: 'uae',
    properties: {
      address: 'Saadiyat Island Villa 5',
      suburb: 'Saadiyat Island',
      state: 'Abu Dhabi',
      postcode: '',
      type: 'villa',
      beds: 5,
      baths: 4,
      parking: 3
    },
    listing_type: 'buy',
    price_or_rent: 9800000,
    description_short: 'Beachfront villa on Saadiyat Island — cultural district, private beach access',
    media: [
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13ea60375-1772166783868.png", type: 'photo', alt: 'Beachfront villa on Saadiyat Island Abu Dhabi for sale' },
    { original_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1731a369c-1772165916873.png", type: 'photo', alt: 'Private beach access at Saadiyat Island villa for sale' }]
  }];


  const fetchListings = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('listings')?.select(`
          *,
          properties:property_id (
            *
          ),
          media (
            *
          )
        `)?.eq('status', 'live');

      if (filters?.mode && filters?.mode !== 'mortgage') {
        query = query?.eq('listing_type', filters?.mode);
      }

      if (filters?.query) {
        query = query?.or(`properties.address.ilike.%${filters?.query}%,properties.suburb.ilike.%${filters?.query}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
      setListings(mockListings);
    } finally {
      setLoading(false);
    }
  };

  const displayListings = (() => {
    if (listings?.length > 0) {
      // Merge Supabase listings with mockListings, preferring Supabase data
      // This ensures mock data fills in any region not covered by Supabase
      const supabaseIds = new Set(listings.map((l) => String(l?.id)));
      const mockFallback = mockListings?.filter((m) => !supabaseIds?.has(String(m?.id)));
      return [...listings, ...mockFallback];
    }
    return mockListings;
  })();
  const filteredListings = displayListings?.filter((l) => {
    const regionMatch = !filters?.region || l?.region === filters?.region;
    const modeMatch = !filters?.mode || filters?.mode === 'mortgage' || l?.listing_type === filters?.mode;

    // Location filter: match suburb or state against the location param
    const locationMatch = !filters?.location || (() => {
      const loc = filters?.location?.toLowerCase();
      const suburb = (l?.properties?.suburb || '')?.toLowerCase();
      const state = (l?.properties?.state || '')?.toLowerCase();
      const address = (l?.properties?.address || '')?.toLowerCase();
      // Map city names to their states/suburbs for broader matching
      const cityStateMap = {
        'sydney': ['nsw', 'sydney', 'bondi', 'parramatta', 'mosman', 'harbour'],
        'melbourne': ['vic', 'melbourne', 'toorak', 'dandenong', 'collins'],
        'brisbane': ['qld', 'brisbane', 'riverside'],
        'perth': ['wa', 'perth', 'fremantle', 'joondalup'],
        'adelaide': ['sa', 'adelaide'],
        'canberra': ['act', 'canberra'],
        'hobart': ['tas', 'hobart'],
        'darwin': ['nt', 'darwin'],
        // UAE Emirates
        'dubai': ['dubai', 'palm jumeirah', 'downtown dubai', 'dubai marina', 'jumeirah village circle', 'business bay', 'difc', 'jvc', 'jbr', 'jumeirah beach residence', 'emirates hills', 'jumeirah bay'],
        'abu dhabi': ['abu dhabi', 'saadiyat island', 'al reem island', 'yas island', 'corniche', 'al maryah island', 'al reem', 'saadiyat'],
        'sharjah': ['sharjah', 'al majaz', 'aljada', 'muwaileh', 'tilal city', 'al nahda'],
        'ajman': ['ajman', 'ajman corniche', 'emirates city', 'al hamidiyah', 'al nuaimiya'],
        'ras al khaimah': ['ras al khaimah', 'al marjan island', 'mina al arab', 'al nakheel', 'hayat island', 'rak'],
        'fujairah': ['fujairah', 'fujairah corniche', 'dibba al fujairah', 'al faseel', 'mina saqr', 'dibba'],
        'umm al quwain': ['umm al quwain', 'uaq marina', 'al salamah', 'uaq free trade zone', 'falaj al mualla', 'uaq']
      };
      const aliases = cityStateMap?.[loc] || [loc];
      return aliases?.some((alias) =>
      suburb?.includes(alias) ||
      state?.includes(alias) ||
      address?.includes(alias)
      );
    })();

    // Facet: price range
    const price = l?.price_or_rent || 0;
    const minOk = !facetFilters?.minPrice || price >= Number(facetFilters?.minPrice);
    const maxOk = !facetFilters?.maxPrice || price <= Number(facetFilters?.maxPrice);

    // Facet: property age (mock — filter by id range as proxy since mock data has no age field)
    // In real data, compare against a `year_built` field
    const ageOk = !facetFilters?.propertyAge || (() => {
      const age = l?.properties?.year_built;
      if (!age) return true; // no age data — show it
      const currentYear = new Date()?.getFullYear();
      const yearsOld = currentYear - age;
      if (facetFilters?.propertyAge === 'new') return yearsOld <= 1;
      if (facetFilters?.propertyAge === '1-5') return yearsOld > 1 && yearsOld <= 5;
      if (facetFilters?.propertyAge === '5-10') return yearsOld > 5 && yearsOld <= 10;
      if (facetFilters?.propertyAge === '10+') return yearsOld > 10;
      return true;
    })();

    // Facet: inspection status (mock — filter by listing inspection_status field)
    const inspOk = !facetFilters?.inspectionStatus || (() => {
      const status = l?.inspection_status || l?.properties?.inspection_status;
      if (!status) return true; // no inspection data — show it
      return status === facetFilters?.inspectionStatus;
    })();

    return regionMatch && modeMatch && locationMatch && minOk && maxOk && ageOk && inspOk;
  });

  const sortedListings = [...(filteredListings || [])]?.sort((a, b) => {
    if (sortBy === 'price_asc') return (a?.price_or_rent || 0) - (b?.price_or_rent || 0);
    if (sortBy === 'price_desc') return (b?.price_or_rent || 0) - (a?.price_or_rent || 0);
    if (sortBy === 'date_desc') return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
    if (sortBy === 'date_asc') return new Date(a?.created_at || 0) - new Date(b?.created_at || 0);
    return 0; // relevance - keep original order
  });

  const handleSaveSearch = () => {
    if (!saveSearchName?.trim()) return;
    const newSearch = {
      id: Date.now(),
      name: saveSearchName?.trim(),
      filters: { ...filters },
      sortBy,
      savedAt: new Date()?.toISOString()
    };
    setSavedSearches((prev) => [newSearch, ...prev]);
    setSaveSearchName('');
    setShowSaveForm(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleLoadSearch = (search) => {
    setFilters(search?.filters);
    setSortBy(search?.sortBy || 'relevance');
    setShowSavedSearches(false);
  };

  const handleDeleteSearch = (id, e) => {
    e?.stopPropagation();
    setSavedSearches((prev) => prev?.filter((s) => s?.id !== id));
  };

  const handleSaveFilterTemplate = (name, currentFacetFilters) => {
    const newTemplate = {
      id: Date.now(),
      name,
      facetFilters: { ...currentFacetFilters },
      mode: filters?.mode,
      savedAt: new Date()?.toISOString()
    };
    setSavedFilterTemplates((prev) => [newTemplate, ...prev]);
  };

  const handleApplyFilterTemplate = (template) => {
    setFacetFilters(template?.facetFilters || { minPrice: '', maxPrice: '', propertyAge: '', inspectionStatus: '' });
    if (template?.mode) {
      setFilters((prev) => ({ ...prev, mode: template?.mode }));
    }
  };

  const handleDeleteFilterTemplate = (id) => {
    setSavedFilterTemplates((prev) => prev?.filter((t) => t?.id !== id));
  };

  const handleToggleCompare = (listing) => {
    setSelectedForCompare((prev) => {
      const exists = prev?.find((p) => p?.id === listing?.id);
      if (exists) return prev?.filter((p) => p?.id !== listing?.id);
      if (prev?.length >= 4) return prev; // max 4
      return [...prev, listing];
    });
  };

  const handleStartComparison = () => {
    localStorage.setItem('listinglens_compare_properties', JSON.stringify(selectedForCompare));
    navigate('/property-comparison', { state: { properties: selectedForCompare } });
  };

  const currentSortLabel = (SORT_OPTIONS?.[language] || SORT_OPTIONS_EN)?.find((o) => o?.value === sortBy)?.label || (language === 'bn' ? 'প্রাসঙ্গিকতা' : 'Relevance');

  const handleLogout = () => {
    navigate('/authentication');
  };

  return (
    <>
      <Helmet>
        <title>Search Results - ListingLens</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={mockUser} onLogout={handleLogout} />
        
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <GoBackButton fallbackPath="/" label="Go Back" className="mr-2" />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {filters?.mode === 'buy' && 'Properties to Buy'}
                {filters?.mode === 'rent' && 'Properties for Rent'}
                {filters?.mode === 'commercial' && 'Commercial Properties'}
                {filters?.mode === 'off-market' && 'Off-Market Opportunities'}
                {filters?.mode === 'mortgage' && 'All Properties'}
              </h1>
              {filters?.region &&
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{
                background: 'rgba(0, 212, 255, 0.15)',
                color: '#00D4FF',
                border: '1px solid rgba(0, 212, 255, 0.3)'
              }}>
                  {filters?.region === 'uae' ? '🇦🇪 UAE' : filters?.region === 'bangladesh' ? '🇧🇩 BD' : '🇦🇺 Australia'}
                </span>
              }
            </div>

            {/* Australian Market Timeline Context Banner */}
            {filters?.year && filters?.region === 'australia' &&
            <div className="mb-4 p-4 rounded-xl" style={{
              background: 'linear-gradient(135deg, rgba(0, 30, 60, 0.95) 0%, rgba(0, 20, 45, 0.98) 100%)',
              border: '2px solid rgba(0, 212, 255, 0.6)',
              boxShadow: '0 0 24px rgba(0, 212, 255, 0.2), inset 0 0 30px rgba(0, 212, 255, 0.04)'
            }}>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🇦🇺</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(0, 212, 255, 0.25)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.5)' }}>
                      {filters?.year} Market
                    </span>
                    <span className="text-sm font-bold" style={{ color: '#00FF88' }}>
                      {filters?.year === '2024' && 'Rate Stabilisation — National median rose 7.1%'}
                      {filters?.year === '2025' && 'Rate Cut Cycle Begins — National median hits $1.02M'}
                      {filters?.year === '2026' && 'Forecast: Continued Growth — 5–8% national growth projected'}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#A8D4F0' }}>
                    {filters?.year === '2024' && 'RBA held cash rate at 4.35% through 2024. Perth & Brisbane led with 18%+ growth. Sydney recovered to new highs above $1.1M.'}
                    {filters?.year === '2025' && 'RBA began cutting rates in Feb 2025. All capitals recording positive growth. Rental vacancy at historic lows of 1.1% nationally.'}
                    {filters?.year === '2026' && 'CoreLogic & PropTrack forecast 5–8% national growth. Sydney projected to reach $1.3M median. Infrastructure spending in QLD (Olympics) and WA (mining) to sustain demand.'}
                  </p>
                  {filters?.location &&
                  <p className="text-xs mt-2 font-semibold" style={{ color: '#00FF88' }}>
                    📍 {filters?.location} — {filters?.year === '2024' && filters?.location === 'Sydney' && 'Sydney median $1.1M+ in 2024'}
                    {filters?.year === '2024' && filters?.location === 'Melbourne' && 'Melbourne median ~$870K in 2024'}
                    {filters?.year === '2024' && filters?.location === 'Brisbane' && 'Brisbane led growth at +18% in 2024'}
                    {filters?.year === '2024' && filters?.location === 'Perth' && 'Perth led WA at +18% growth in 2024'}
                    {filters?.year === '2025' && filters?.location === 'Sydney' && 'Sydney median $1.18M in 2025'}
                    {filters?.year === '2025' && filters?.location === 'Melbourne' && 'Melbourne median $920K in 2025'}
                    {filters?.year === '2025' && filters?.location === 'Brisbane' && 'Brisbane +11.4% YoY in 2025'}
                    {filters?.year === '2025' && filters?.location === 'Perth' && 'Perth +19.6% YoY — top performer in 2025'}
                    {filters?.year === '2026' && filters?.location === 'Sydney' && 'Sydney projected to reach $1.3M median'}
                    {filters?.year === '2026' && filters?.location === 'Melbourne' && 'Melbourne forecast strong growth in 2026'}
                    {filters?.year === '2026' && filters?.location === 'Brisbane' && 'Brisbane Olympics infrastructure driving 2026 demand'}
                    {filters?.year === '2026' && filters?.location === 'Perth' && 'Perth WA mining sector sustaining strong demand in 2026'}
                  </p>
                  }
                </div>
              </div>
            </div>
            }

            {/* Region toggle */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setFilters({ ...filters, region: 'australia', location: '' })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: filters?.region === 'australia' ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
                  color: filters?.region === 'australia' ? '#00D4FF' : '#6B9EC4',
                  border: filters?.region === 'australia' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(0, 212, 255, 0.1)'
                }}>
                
                🇦🇺 Australia
              </button>
              <button
                onClick={() => setFilters({ ...filters, region: 'uae', location: '' })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: filters?.region === 'uae' ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
                  color: filters?.region === 'uae' ? '#00D4FF' : '#6B9EC4',
                  border: filters?.region === 'uae' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(0, 212, 255, 0.1)'
                }}>
                
                🇦🇪 UAE
              </button>
            </div>

            {filters?.query &&
            <p className="text-muted-foreground mb-3">
                Showing results for "{filters?.query}"
              </p>
            }

            {/* Sort + Saved Searches toolbar */}
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {/* Sort Dropdown */}
              <div className="relative sort-dropdown-container">
                <button
                  onClick={() => setShowSortDropdown((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: 'rgba(0, 212, 255, 0.08)',
                    color: '#00D4FF',
                    border: '1px solid rgba(0, 212, 255, 0.2)'
                  }}>
                  
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M7 12h10M11 18h2" />
                  </svg>
                  {t('sortBy')}: {currentSortLabel}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-5" />
                  </svg>
                </button>
                {showSortDropdown &&
                <div
                  className="absolute top-full left-0 mt-1 w-52 rounded-xl shadow-2xl z-50 overflow-hidden"
                  style={{ background: '#0D1B2A', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                  
                    {(SORT_OPTIONS?.[language] || SORT_OPTIONS_EN)?.map((opt) =>
                  <button
                    key={opt?.value}
                    onClick={() => {setSortBy(opt?.value);setShowSortDropdown(false);}}
                    className="w-full text-left px-4 py-2.5 text-sm transition-all flex items-center justify-between"
                    style={{
                      color: sortBy === opt?.value ? '#00D4FF' : '#8BA5C0',
                      background: sortBy === opt?.value ? 'rgba(0, 212, 255, 0.1)' : 'transparent'
                    }}>
                    
                        {opt?.label}
                        {sortBy === opt?.value &&
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                    }
                      </button>
                  )}
                  </div>
                }
              </div>

              {/* Save Search Button */}
              <div className="relative saved-searches-container">
                <button
                  onClick={() => {setShowSaveForm((v) => !v);setShowSavedSearches(false);}}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: saveSuccess ? 'rgba(0, 212, 255, 0.2)' : 'rgba(0, 212, 255, 0.08)',
                    color: saveSuccess ? '#00D4FF' : '#8BA5C0',
                    border: '1px solid rgba(0, 212, 255, 0.2)'
                  }}>
                  
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
                  </svg>
                  {saveSuccess ? language === 'bn' ? 'সংরক্ষিত!' : 'Saved!' : t('saveSearch')}
                </button>

                {showSaveForm &&
                <div
                  className="absolute top-full left-0 mt-1 w-64 rounded-xl shadow-2xl z-50 p-3"
                  style={{ background: '#0D1B2A', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                  
                    <p className="text-xs font-semibold mb-2" style={{ color: '#00D4FF' }}>{t('savedSearches')}</p>
                    <input
                    type="text"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e?.target?.value)}
                    onKeyDown={(e) => e?.key === 'Enter' && handleSaveSearch()}
                    placeholder="e.g. Sydney 3BR under $2M"
                    className="w-full px-3 py-2 rounded-lg text-sm mb-2 outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      color: '#E2E8F0'
                    }}
                    autoFocus />
                  
                    <div className="flex gap-2">
                      <button
                      onClick={handleSaveSearch}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{ background: 'rgba(0, 212, 255, 0.2)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
                      
                        Save
                      </button>
                      <button
                      onClick={() => {setShowSaveForm(false);setSaveSearchName('');}}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(255,255,255,0.05)', color: '#8BA5C0', border: '1px solid rgba(255,255,255,0.1)' }}>
                      
                        Cancel
                      </button>
                    </div>
                  </div>
                }
              </div>

              {/* Saved Searches List Button */}
              {savedSearches?.length > 0 ?
              <div className="relative saved-searches-container">
                  <button
                  onClick={() => {setShowSavedSearches((v) => !v);setShowSaveForm(false);}}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: 'rgba(0, 212, 255, 0.08)',
                    color: '#8BA5C0',
                    border: '1px solid rgba(0, 212, 255, 0.2)'
                  }}>
                  
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    My Searches
                    <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold"
                    style={{ background: 'rgba(0, 212, 255, 0.3)', color: '#00D4FF' }}>
                    
                      {savedSearches?.length}
                    </span>
                  </button>

                  {showSavedSearches &&
                <div
                  className="absolute top-full left-0 mt-1 w-72 rounded-xl shadow-2xl z-50 overflow-hidden"
                  style={{ background: '#0D1B2A', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                  
                      <div className="px-4 py-2.5" style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)' }}>
                        <p className="text-xs font-semibold" style={{ color: '#00D4FF' }}>{t('savedSearches')}</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {savedSearches?.map((search) =>
                    <button
                      key={search?.id}
                      onClick={() => handleLoadSearch(search)}
                      className="w-full text-left px-4 py-3 transition-all group"
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.06)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>{search?.name}</p>
                                <p className="text-xs mt-0.5" style={{ color: '#6B9EC4' }}>
                                  {search?.filters?.mode?.toUpperCase()} · {search?.filters?.region === 'uae' ? '🇦🇪 UAE' : search?.filters?.region === 'bangladesh' ? '🇧🇩 BD' : '🇦🇺 AU'} · {(SORT_OPTIONS?.[language] || SORT_OPTIONS_EN)?.find((o) => o?.value === (search?.sortBy || 'relevance'))?.label}
                                </p>
                              </div>
                              <button
                          onClick={(e) => handleDeleteSearch(search?.id, e)}
                          className="flex-shrink-0 p-1 rounded transition-all opacity-0 group-hover:opacity-100"
                          style={{ color: '#FF6B6B' }}
                          title="Delete">
                          
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </button>
                    )}
                      </div>
                    </div>
                }
              </div> :
              null}

              {/* Results count */}
              <span className="text-sm ml-auto" style={{ color: '#6B9EC4' }}>
                {sortedListings?.length} {sortedListings?.length === 1 ? 'property' : 'properties'} found
              </span>
            </div>
          </div>

          <SearchFilters filters={filters} onFiltersChange={setFilters} onSearch={fetchListings} />

          <FacetedFilterPanel
            facetFilters={facetFilters}
            onFacetFiltersChange={setFacetFilters}
            savedTemplates={savedFilterTemplates}
            onSaveTemplate={handleSaveFilterTemplate}
            onApplyTemplate={handleApplyFilterTemplate}
            onDeleteTemplate={handleDeleteFilterTemplate}
            mode={filters?.mode} />
          

          {loading ?
          <SearchResultsSkeleton count={6} /> :
          sortedListings?.length === 0 ?
          <SearchEmptyState
            query={filters?.query}
            onClearFilters={() => {
              setFilters((prev) => ({ ...prev, query: '', minPrice: '', maxPrice: '', beds: '', baths: '', propertyType: '' }));
              setFacetFilters({ minPrice: '', maxPrice: '', propertyAge: '', inspectionStatus: '' });
            }} /> :


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedListings?.map((listing) =>
            <PropertyCard
              key={listing?.id}
              listing={listing}
              onClick={() => {
                sessionStorage.setItem('listinglens_search_ids', JSON.stringify(sortedListings?.map((l) => l?.id)));
                sessionStorage.setItem('listinglens_search_url', window.location?.pathname + window.location?.search);
                navigate(`/listing/${listing?.id}`);
              }}
              compareMode={true}
              isSelected={!!selectedForCompare?.find((p) => p?.id === listing?.id)}
              onToggleSelect={handleToggleCompare} />

            )}
            </div>

          }
        </main>

        {/* Compare floating bar */}
        {selectedForCompare?.length >= 2 &&
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-2xl shadow-2xl"
          style={{ background: '#0D1B2A', border: '1px solid rgba(0, 212, 255, 0.4)', backdropFilter: 'blur(12px)' }}>
          
            <div className="flex items-center gap-2">
              <div
              className="flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold"
              style={{ background: 'rgba(0, 212, 255, 0.2)', color: '#00D4FF' }}>
              
                {selectedForCompare?.length}
              </div>
              <span className="text-sm font-medium" style={{ color: '#E2E8F0' }}>
                {selectedForCompare?.length === 1 ? 'property' : 'properties'} selected
              </span>
            </div>
            <button
            onClick={handleStartComparison}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{ background: 'rgba(0, 212, 255, 0.9)', color: '#0A1628' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.9)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.9)'}>
            
              <Icon name="BarChart2" size={16} color="currentColor" />
              Compare Properties
            </button>
            <button
            onClick={() => setSelectedForCompare([])}
            className="p-1.5 rounded-lg transition-all"
            style={{ color: '#6B9EC4' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#FF6B6B'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6B9EC4'}
            title="Clear selection">
            
              <Icon name="X" size={16} color="currentColor" />
            </button>
          </div>
        }
      </div>
    </>);


};

export default SearchResults;